import { useCallback, useEffect, useRef, useState } from 'react';
import { type CallMode, type IncomingCall } from '../services/callApi';
import { createCallSocket, type CallSocket, type CallSocketMessage } from '../services/callSocket';
import { createPeerConnection, getCallMedia } from '../services/webrtc';
import type { Username } from '../utils/constants';
import { toFriendlyError } from '../utils/errors';

type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended';

export type CallQuality = {
  bitrateKbps: number | null;
  packetsLost: number | null;
  latencyMs: number | null;
  label: 'unknown' | 'good' | 'fair' | 'poor';
};

export function useCall(token: string | null, username: Username | null) {
  const [phase, setPhase] = useState<CallPhase>('idle');
  const [mode, setMode] = useState<CallMode>('voice');
  const [callId, setCallId] = useState('');
  const [peerName, setPeerName] = useState('');
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Ready');
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [speakerOff, setSpeakerOff] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [quality, setQuality] = useState<CallQuality>({ bitrateKbps: null, packetsLost: null, latencyMs: null, label: 'unknown' });
  const [reconnecting, setReconnecting] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const socketRef = useRef<CallSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(false);
  const localStreamRef = useRef<MediaStream | null>(null);
  const localTrackIdsRef = useRef<Set<string>>(new Set());
  const phaseRef = useRef<CallPhase>('idle');
  const callIdRef = useRef('');
  const intervalsRef = useRef<number[]>([]);
  const connectedAtRef = useRef<number | null>(null);
  const lastBytesRef = useRef<{ timestamp: number; bytes: number } | null>(null);
  const pendingStartRef = useRef<((message: Extract<CallSocketMessage, { type: 'call-started' }>) => void) | null>(null);
  const pendingStartRejectRef = useRef<((reason: Error) => void) | null>(null);
  const pendingOfferRef = useRef<((message: Extract<CallSocketMessage, { type: 'offer' }>) => void) | null>(null);
  const pendingAnswerRef = useRef<((message: Extract<CallSocketMessage, { type: 'answer' }>) => void) | null>(null);
  const storedOfferRef = useRef<Extract<CallSocketMessage, { type: 'offer' }> | null>(null);
  const storedAnswerRef = useRef<Extract<CallSocketMessage, { type: 'answer' }> | null>(null);
  const storedIceRef = useRef<Extract<CallSocketMessage, { type: 'ice' }>[]>([]);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);


  const clearTimers = useCallback(() => {
    intervalsRef.current.forEach((timer) => window.clearInterval(timer));
    intervalsRef.current = [];
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    clearTimers();
    peerRef.current?.close();
    peerRef.current = null;
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    localTrackIdsRef.current.clear();
    setLocalStream(null);
    setRemoteStream(null);
    setCallId('');
    callIdRef.current = '';
    setMuted(false);
    setCameraOff(false);
    setSpeakerOff(false);
    setDurationSeconds(0);
    setQuality({ bitrateKbps: null, packetsLost: null, latencyMs: null, label: 'unknown' });
    setReconnecting(false);
    setVerificationCode('');
    connectedAtRef.current = null;
    lastBytesRef.current = null;
  }, [clearTimers]);

  const createPeer = useCallback(
    (activeCallId: string, socket: CallSocket, activeMode: CallMode) => {
      const peer = createPeerConnection();
      const nextRemoteStream = new MediaStream();
      setRemoteStream(nextRemoteStream);
      peer.ontrack = (event) => {
        console.info('CALL_REMOTE_STREAM');
        const tracks = event.streams[0]?.getTracks() ?? [event.track];
        tracks.forEach((track) => {
          if (localTrackIdsRef.current.has(track.id)) {
            console.info('CALL_REMOTE_SELF_TRACK_BLOCKED', { kind: track.kind });
            return;
          }

          if (!nextRemoteStream.getTracks().some((current) => current.id === track.id)) {
            nextRemoteStream.addTrack(track);
          }

          if (track.kind === 'audio') {
            console.info('CALL_REMOTE_TRACK_RECEIVED kind=audio');
            if (activeMode === 'voice') {
              console.info('VOICE_REMOTE_TRACK_RECEIVED', { kind: track.kind, id: track.id });
            }
          }
          console.info('CALL_REMOTE_TRACK_RECEIVED', { kind: track.kind, id: track.id });
        });
        logStreamTracks('CALL_REMOTE_STREAM_TRACKS', nextRemoteStream);
        setRemoteStream(new MediaStream(nextRemoteStream.getTracks()));
      };
      peer.onconnectionstatechange = () => {
        setStatus(peer.connectionState);
        if (peer.connectionState === 'connected') {
          connectedAtRef.current = connectedAtRef.current ?? Date.now();
          setPhase('active');
        }
        setReconnecting(peer.connectionState === 'connecting' || peer.connectionState === 'disconnected');
        if (peer.connectionState === 'failed') {
          setError('Connection failed. Network may block peer-to-peer calls.');
        }
      };
      peer.oniceconnectionstatechange = () => {
        setStatus(`${peer.connectionState} / ICE ${peer.iceConnectionState}`);
        setReconnecting(peer.iceConnectionState === 'checking' || peer.iceConnectionState === 'disconnected');
      };
      peer.onicecandidate = (event) => {
        if (event.candidate) {
          socket.send({ type: 'ice', callId: activeCallId, candidate: event.candidate.toJSON() });
        }
      };
      peerRef.current = peer;
      return peer;
    },
    [],
  );

  const addLocalTracks = useCallback((peer: RTCPeerConnection, stream: MediaStream, peerRole: 'caller' | 'receiver', activeMode: CallMode) => {
    stream.getTracks().forEach((track) => {
      localTrackIdsRef.current.add(track.id);

      if (track.kind === 'audio') {
        track.enabled = true;
      }

      peer.addTrack(track, stream);

      if (track.kind === 'audio') {
        console.info('CALL_LOCAL_AUDIO_TRACK_ADDED', { peerRole, username });
        if (activeMode === 'voice') {
          console.info('VOICE_LOCAL_AUDIO_TRACK_ADDED', { peerRole, username });
        }
        console.info('CALL_LOCAL_AUDIO_TRACK_STATE', {
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
        });
      }
    });
    const hasAudioSender = peer.getSenders().some((sender) => sender.track?.kind === 'audio');
    console.info('CALL_SENDER_AUDIO_TRACK_PRESENT', hasAudioSender);
    console.info('CALL_RECEIVER_AUDIO_TRACK_PRESENT', peerRole === 'receiver' ? hasAudioSender : true);
  }, [username]);

  const startStats = useCallback(() => {
    const timer = window.setInterval(async () => {
      const peer = peerRef.current;

      if (!peer) {
        return;
      }

      const stats = await peer.getStats().catch(() => null);

      if (!stats) {
        return;
      }

      let bytesReceived = 0;
      let packetsLost = 0;
      let latencyMs: number | null = null;

      stats.forEach((report) => {
        const entry = report as RTCStats & Record<string, unknown>;

        if (entry.type === 'inbound-rtp' && entry.isRemote !== true) {
          bytesReceived += typeof entry.bytesReceived === 'number' ? entry.bytesReceived : 0;
          packetsLost += typeof entry.packetsLost === 'number' ? entry.packetsLost : 0;
        }

        if (entry.type === 'candidate-pair' && entry.state === 'succeeded' && entry.nominated === true) {
          latencyMs =
            typeof entry.currentRoundTripTime === 'number' ? Math.round(entry.currentRoundTripTime * 1000) : latencyMs;
        }
      });

      const now = Date.now();
      const lastBytes = lastBytesRef.current;
      let bitrateKbps: number | null = null;

      if (lastBytes && now > lastBytes.timestamp) {
        bitrateKbps = Math.max(0, Math.round(((bytesReceived - lastBytes.bytes) * 8) / (now - lastBytes.timestamp)));
      }

      lastBytesRef.current = { timestamp: now, bytes: bytesReceived };

      const label =
        latencyMs !== null && latencyMs > 450 ? 'poor' : packetsLost > 20 ? 'fair' : bitrateKbps !== null && bitrateKbps < 24 ? 'fair' : 'good';

      setQuality({ bitrateKbps, packetsLost, latencyMs, label });

      if (connectedAtRef.current) {
        setDurationSeconds(Math.floor((now - connectedAtRef.current) / 1000));
      }
    }, 3000);
    intervalsRef.current.push(timer);
  }, []);

  const getLoggedMedia = useCallback(async (nextMode: CallMode) => {
    try {
      console.info('CALL_GET_USER_MEDIA_AUDIO_START');
      const stream = await getCallMedia(nextMode);
      logStreamTracks('CALL_LOCAL_STREAM_TRACKS', stream);
      logLocalAudioTrackStates(stream);
      console.info('CALL_GET_USER_MEDIA_AUDIO_OK');
      console.info('CALL_GET_USER_MEDIA_OK');
      return stream;
    } catch (caught) {
      console.info('CALL_GET_USER_MEDIA_AUDIO_ERROR');
      console.info('CALL_GET_USER_MEDIA_ERROR');
      throw caught;
    }
  }, []);

  const handleSocketMessage = useCallback(
    (message: CallSocketMessage) => {
      if (message.type === 'incoming-call') {
        if (phaseRef.current !== 'active' && phaseRef.current !== 'connecting' && phaseRef.current !== 'outgoing') {
          console.info('CALL_WS_INCOMING');
          console.info('CALL_INCOMING_OVERLAY_SHOW');
          if (new URLSearchParams(window.location.search).get('callId') === message.callId) {
            console.info('CALL_NOTIFICATION_RESTORED_INCOMING_CALL');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          setIncoming({
            incoming: true,
            callId: message.callId,
            caller: message.caller,
            mode: message.mode,
            verificationCode: message.verificationCode,
          });
          setVerificationCode(message.verificationCode ?? '');
          setPhase('incoming');
        }
        return;
      }

      if (message.type === 'call-started') {
        pendingStartRef.current?.(message);
        pendingStartRef.current = null;
        return;
      }

      if (message.type === 'offer') {
        if (pendingOfferRef.current) {
          pendingOfferRef.current(message);
          pendingOfferRef.current = null;
        } else {
          storedOfferRef.current = message;
        }
        return;
      }

      if (message.type === 'answer') {
        if (pendingAnswerRef.current) {
          pendingAnswerRef.current(message);
          pendingAnswerRef.current = null;
        } else {
          storedAnswerRef.current = message;
        }
        return;
      }

      if (message.type === 'ice') {
        const peer = peerRef.current;

        if (!peer || message.callId !== callIdRef.current || !peer.remoteDescription) {
          storedIceRef.current.push(message);
          return;
        }

        peer.addIceCandidate(new RTCIceCandidate(message.candidate)).catch(() => undefined);
        return;
      }

      if (message.type === 'call-accepted') {
        setStatus('Connecting');
        return;
      }

      if (message.type === 'call-rejected') {
        cleanup();
        setPhase('ended');
        setStatus('Call rejected');
        return;
      }

      if (message.type === 'call-ended') {
        cleanup();
        setPhase('ended');
        setStatus('Call ended');
        return;
      }

      if (message.type === 'call-error') {
        setError(message.message);
        pendingStartRejectRef.current?.(new Error(message.message));
        pendingStartRejectRef.current = null;
        pendingStartRef.current = null;
      }
    },
    [cleanup],
  );

  const ensureSocket = useCallback(async () => {
    if (!username) {
      throw new Error('Please log in before starting a call.');
    }

    const current = socketRef.current;

    if (current && current.socket.readyState !== WebSocket.CLOSED && current.socket.readyState !== WebSocket.CLOSING) {
      await current.ready;
      return current;
    }

    clearReconnectTimer();
    const next = createCallSocket(username, {
      onMessage: handleSocketMessage,
      onError: (message) => {
        if (phaseRef.current !== 'idle') {
          setError(message);
        }
      },
      onOpen: () => {
        reconnectAttemptsRef.current = 0;
        clearReconnectTimer();
      },
      onClose: () => {
        socketRef.current = null;
        if (!shouldReconnectRef.current) {
          return;
        }

        const delay = Math.min(30000, 1000 * 2 ** reconnectAttemptsRef.current);
        reconnectAttemptsRef.current += 1;
        console.info('CALL_WS_RECONNECT');
        reconnectTimerRef.current = window.setTimeout(() => {
          void ensureSocket().catch(() => undefined);
        }, delay);
      },
    });
    socketRef.current = next;
    await next.ready;
    return next;
  }, [clearReconnectTimer, handleSocketMessage, username]);

  const waitForStart = useCallback(() => {
    return new Promise<Extract<CallSocketMessage, { type: 'call-started' }>>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        pendingStartRef.current = null;
        pendingStartRejectRef.current = null;
        reject(new Error('The other user is not online yet.'));
      }, 20000);

      pendingStartRef.current = (message) => {
        window.clearTimeout(timeout);
        pendingStartRejectRef.current = null;
        resolve(message);
      };
      pendingStartRejectRef.current = (reason) => {
        window.clearTimeout(timeout);
        reject(reason);
      };
    });
  }, []);

  const waitForOffer = useCallback((activeCallId: string) => {
    const stored = storedOfferRef.current;

    if (stored?.callId === activeCallId) {
      storedOfferRef.current = null;
      return Promise.resolve(stored);
    }

    return new Promise<Extract<CallSocketMessage, { type: 'offer' }>>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        pendingOfferRef.current = null;
        reject(new Error('Call expired. Please try again.'));
      }, 30000);

      pendingOfferRef.current = (message) => {
        if (message.callId !== activeCallId) {
          return;
        }

        window.clearTimeout(timeout);
        resolve(message);
      };
    });
  }, []);

  const waitForAnswer = useCallback((activeCallId: string) => {
    const stored = storedAnswerRef.current;

    if (stored?.callId === activeCallId) {
      storedAnswerRef.current = null;
      return Promise.resolve(stored);
    }

    return new Promise<Extract<CallSocketMessage, { type: 'answer' }>>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        pendingAnswerRef.current = null;
        reject(new Error('Connection failed. Receiver did not answer in time.'));
      }, 30000);

      pendingAnswerRef.current = (message) => {
        if (message.callId !== activeCallId) {
          return;
        }

        window.clearTimeout(timeout);
        resolve(message);
      };
    });
  }, []);

  const start = useCallback(
    async (nextMode: CallMode) => {
      console.info('CALL_BUTTON_CLICK', nextMode);
      if (!token || !username) {
        return;
      }

      cleanup();
      setError('');
      setMode(nextMode);
      setPhase('outgoing');
      setStatus('Requesting media');

      try {
        const socket = await ensureSocket();
        const startedPromise = waitForStart();
        console.info('CALL_INVITE_SEND');
        socket.send({ type: 'call-start', mode: nextMode });
        const started = await startedPromise;
        setCallId(started.callId);
        callIdRef.current = started.callId;
        setPeerName(started.receiver);
        setVerificationCode(started.verificationCode ?? '');
        setStatus('Requesting media');
        const stream = await getLoggedMedia(nextMode);
        localStreamRef.current = stream;
        setLocalStream(stream);
        const peer = createPeer(started.callId, socket, nextMode);
        addLocalTracks(peer, stream, 'caller', nextMode);
        startStats();
        const offer = await peer.createOffer();
        console.info('CALL_PEER_OFFER_CREATED');
        await peer.setLocalDescription(offer);
        await waitForIceGatheringComplete(peer);
        socket.send({ type: 'offer', callId: started.callId, offer: peer.localDescription?.toJSON() ?? offer });
        setStatus(started.warning || 'Calling. This works best when both users have the app open.');

        const answer = await waitForAnswer(started.callId);
        if (peer.signalingState !== 'stable') {
          await peer.setRemoteDescription(new RTCSessionDescription(answer.answer));
          await flushStoredIce(peer, started.callId, storedIceRef.current);
          setPhase('connecting');
          setStatus('Connecting');
        }
      } catch (caught) {
        const activeCallId = callIdRef.current;
        if (activeCallId) {
          const socket = await ensureSocket().catch(() => null);
          socket?.send({ type: 'call-end', callId: activeCallId });
        }
        setPhase('idle');
        setError(toFriendlyError(caught));
        cleanup();
      }
    },
    [addLocalTracks, cleanup, createPeer, ensureSocket, getLoggedMedia, startStats, token, username, waitForAnswer, waitForStart],
  );

  const accept = useCallback(async () => {
    console.info('CALL_ACCEPT_CLICK');
    if (!token || !username || !incoming) {
      return;
    }

    cleanup();
    setError('');
    setPhase('connecting');
    setMode(incoming.mode);
    setCallId(incoming.callId);
    callIdRef.current = incoming.callId;
    setPeerName(incoming.caller);
    setVerificationCode(incoming.verificationCode ?? '');

    try {
      const socket = await ensureSocket();
      const stream = await getLoggedMedia(incoming.mode);
      localStreamRef.current = stream;
      setLocalStream(stream);
      socket.send({ type: 'call-accept', callId: incoming.callId });
      const peer = createPeer(incoming.callId, socket, incoming.mode);
      addLocalTracks(peer, stream, 'receiver', incoming.mode);
      startStats();
      setStatus('Waiting for secure offer');

      const offer = await waitForOffer(incoming.callId);
      await peer.setRemoteDescription(new RTCSessionDescription(offer.offer));
      await flushStoredIce(peer, incoming.callId, storedIceRef.current);
      const answer = await peer.createAnswer();
      console.info('CALL_PEER_ANSWER_CREATED');
      await peer.setLocalDescription(answer);
      await waitForIceGatheringComplete(peer);
      socket.send({ type: 'answer', callId: incoming.callId, answer: peer.localDescription?.toJSON() ?? answer });
      setIncoming(null);
      setVerificationCode(incoming.verificationCode ?? '');
      setStatus('Connecting');
    } catch (caught) {
      setError(toFriendlyError(caught));
      cleanup();
      setPhase('idle');
    }
  }, [addLocalTracks, cleanup, createPeer, ensureSocket, getLoggedMedia, incoming, startStats, token, username, waitForOffer]);

  const reject = useCallback(async () => {
    console.info('CALL_REJECT_CLICK');
    if (token && username && incoming) {
      const socket = await ensureSocket().catch(() => null);
      socket?.send({ type: 'call-reject', callId: incoming.callId });
    }
    setIncoming(null);
    setPhase('idle');
  }, [ensureSocket, incoming, token, username]);

  const end = useCallback(async () => {
    const activeCallId = callIdRef.current;
    cleanup();
    setPhase('ended');
    setStatus('Call ended');
    if (token && username && activeCallId) {
      const socket = await ensureSocket().catch(() => null);
      socket?.send({ type: 'call-end', callId: activeCallId });
    }
  }, [cleanup, ensureSocket, token, username]);

  const toggleMute = useCallback(() => {
    const enabled = muted;
    console.info('CALL_MUTE_TOGGLE');
    localStreamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
      console.info('CALL_AUDIO_TRACK_ENABLED_STATE', track.enabled);
      console.info('CALL_MUTE_STATE', { enabled: track.enabled });
    });
    setMuted(!muted);
  }, [muted]);

  const toggleCamera = useCallback(() => {
    const enabled = cameraOff;
    localStreamRef.current?.getVideoTracks().forEach((track) => {
      track.enabled = enabled;
    });
    setCameraOff(!cameraOff);
  }, [cameraOff]);

  const toggleSpeaker = useCallback(() => {
    console.info('CALL_SPEAKER_TOGGLE_CLICK');
    setSpeakerOff((current) => !current);
  }, []);

  const switchCamera = useCallback(async () => {
    console.info('CALL_CAMERA_SWITCH_CLICK');
    if (mode !== 'video') {
      return;
    }

    const nextFacingMode = facingMode === 'user' ? 'environment' : 'user';
    const sender = peerRef.current?.getSenders().find((item) => item.track?.kind === 'video');
    const oldTrack = localStreamRef.current?.getVideoTracks()[0];

    try {
      if (!sender || !oldTrack) {
        setError('Camera switching is not available yet.');
        return;
      }

      const nextStream = await getReplacementVideoStream(nextFacingMode, oldTrack);
      const nextTrack = nextStream.getVideoTracks()[0];

      if (!nextTrack) {
        nextStream.getTracks().forEach((track) => track.stop());
        setError('Camera switching is not supported on this device.');
        return;
      }

      await sender.replaceTrack(nextTrack);
      oldTrack?.stop();

      const currentStream = localStreamRef.current;

      if (currentStream) {
        const audioTrackIds = currentStream.getAudioTracks().map((track) => track.id).join(',');
        currentStream.getVideoTracks().forEach((track) => currentStream.removeTrack(track));
        currentStream.addTrack(nextTrack);
        setLocalStream(new MediaStream(currentStream.getTracks()));
        localStreamRef.current = currentStream;
        const preservedAudioTrackIds = currentStream.getAudioTracks().map((track) => track.id).join(',');
        console.info('CALL_CAMERA_SWITCH_AUDIO_PRESERVED', audioTrackIds === preservedAudioTrackIds);
      }

      setFacingMode(nextFacingMode);
      setCameraOff(false);
      console.info('CALL_CAMERA_SWITCH_OK');
    } catch {
      console.info('CALL_CAMERA_SWITCH_ERROR');
      setError('Camera switching is not supported or permission was denied.');
    }
  }, [facingMode, mode]);

  useEffect(() => {
    if (!token || !username) {
      shouldReconnectRef.current = false;
      clearReconnectTimer();
      socketRef.current?.close();
      socketRef.current = null;
      return;
    }

    let cancelled = false;
    shouldReconnectRef.current = true;
    ensureSocket().catch((reason) => {
      if (!cancelled) {
        setError(toFriendlyError(reason));
      }
    });

    const reconnectNow = () => {
      if (!cancelled && shouldReconnectRef.current) {
        console.info('CALL_WS_RECONNECT');
        void ensureSocket().catch(() => undefined);
      }
    };

    window.addEventListener('online', reconnectNow);
    window.addEventListener('focus', reconnectNow);
    document.addEventListener('visibilitychange', reconnectNow);

    return () => {
      cancelled = true;
      shouldReconnectRef.current = false;
      clearReconnectTimer();
      window.removeEventListener('online', reconnectNow);
      window.removeEventListener('focus', reconnectNow);
      document.removeEventListener('visibilitychange', reconnectNow);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [clearReconnectTimer, ensureSocket, token, username]);

  useEffect(() => cleanup, [cleanup]);

  return {
    phase,
    mode,
    callId,
    peerName,
    incoming,
    localStream,
    remoteStream,
    error,
    status,
    muted,
    cameraOff,
    speakerOff,
    facingMode,
    durationSeconds,
    quality,
    reconnecting,
    verificationCode,
    start,
    accept,
    reject,
    end,
    toggleMute,
    toggleCamera,
    toggleSpeaker,
    switchCamera,
  };
}

function waitForIceGatheringComplete(peer: RTCPeerConnection) {
  if (peer.iceGatheringState === 'complete') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const timeout = window.setTimeout(done, 2500);

    function done() {
      window.clearTimeout(timeout);
      peer.removeEventListener('icegatheringstatechange', onStateChange);
      resolve();
    }

    function onStateChange() {
      if (peer.iceGatheringState === 'complete') {
        done();
      }
    }

    peer.addEventListener('icegatheringstatechange', onStateChange);
  });
}

async function flushStoredIce(
  peer: RTCPeerConnection,
  callId: string,
  storedIce: Extract<CallSocketMessage, { type: 'ice' }>[],
) {
  const remaining: Extract<CallSocketMessage, { type: 'ice' }>[] = [];

  for (const message of storedIce) {
    if (message.callId !== callId) {
      remaining.push(message);
      continue;
    }

    await peer.addIceCandidate(new RTCIceCandidate(message.candidate)).catch(() => undefined);
  }

  storedIce.splice(0, storedIce.length, ...remaining);
}

async function getReplacementVideoStream(nextFacingMode: 'user' | 'environment', currentTrack: MediaStreamTrack) {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: nextFacingMode } },
    });
  } catch {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((device) => device.kind === 'videoinput');
    const currentDeviceId = currentTrack.getSettings().deviceId;

    if (cameras.length < 2) {
      throw new Error('No alternate camera found.');
    }

    const currentIndex = cameras.findIndex((device) => device.deviceId === currentDeviceId);
    const nextCamera = cameras[(currentIndex >= 0 ? currentIndex + 1 : 0) % cameras.length];

    if (!nextCamera?.deviceId) {
      throw new Error('No alternate camera found.');
    }

    return navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { deviceId: { exact: nextCamera.deviceId } },
    });
  }
}

function logStreamTracks(label: string, stream: MediaStream) {
  console.info(label, {
    audioCount: stream.getAudioTracks().length,
    videoCount: stream.getVideoTracks().length,
  });
}

function logLocalAudioTrackStates(stream: MediaStream) {
  stream.getAudioTracks().forEach((track) => {
    console.info('CALL_LOCAL_AUDIO_TRACK_STATE', {
      enabled: track.enabled,
      muted: track.muted,
      readyState: track.readyState,
    });
  });
}
